import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { selectUser } from "../../../features/userSlice";
import { Card, PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../../components/UI/States";
import { Notice, Pill, SearchField, Segmented } from "../../../components/Fleet/FleetParts";
import { formatDate, formatDateTime } from "../../../components/Fleet/fleetFormat";
import { askAgain, fetchFeedbackOverview, fetchFeedbackResponses } from "./feedbackApi";

// The monthly feedback check-in (Admin / IT): what people sent from the popup
// every signed-in user gets once a month, who dismissed it, and when each
// person sees it next. Sending or dismissing starts their next wait; the
// popup is components/Other/FeedbackCheckIn.js.

const PEOPLE_FILTERS = [
  { value: "all", label: "All" },
  { value: "sent", label: "Sent feedback" },
  { value: "dismissed", label: "Dismissed" },
  { value: "due", label: "Due now" },
];

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

const matches = (person, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [person.name, person.email, person.branch].some((value) =>
    String(value || "").toLowerCase().includes(q)
  );
};

const LastAnswer = ({ person }) => {
  if (person.lastAction === "sent") return <Pill tone="emerald">Sent feedback</Pill>;
  if (person.lastAction === "dismissed") return <Pill tone="amber">Dismissed</Pill>;
  return <Pill tone="slate">No answer yet</Pill>;
};

// --------------------------------------------------------------- responses

const ResponsesPanel = ({ userId }) => {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Search once they pause typing.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    setRows(null);
    setError(null);
    fetchFeedbackResponses(userId, { q: search })
      .then((data) => {
        if (!live) return;
        setRows(data.responses);
        setHasMore(data.hasMore);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [userId, search]);

  const loadMore = () => {
    if (!rows?.length) return;
    setLoadingMore(true);
    fetchFeedbackResponses(userId, { q: search, before: rows[rows.length - 1].at })
      .then((data) => {
        setRows((current) => [...current, ...data.responses]);
        setHasMore(data.hasMore);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMore(false));
  };

  return (
    <>
      <div className="mb-4">
        <SearchField value={query} onChange={setQuery} placeholder="Search person, branch or words…" />
      </div>
      {error && <Notice tone="rose">{error}</Notice>}
      <Card padded={false} className="overflow-hidden">
        {!rows && !error ? (
          <LoadingState label="Loading responses…" />
        ) : rows?.length === 0 ? (
          <EmptyState
            compact
            icon={ChatBubbleOutlineRoundedIcon}
            title={search ? "Nothing matches that." : "No feedback yet."}
            description="What people send from the monthly check-in popup shows up here, newest first."
          />
        ) : (
          <ul className="divide-y divide-[color:var(--app-divider)]">
            {(rows || []).map((row) => (
              <li key={row.id} className="px-4 py-3 sm:px-5">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{row.name}</span>
                  {row.branch && <Pill tone="slate">{row.branch}</Pill>}
                  <span className="app-muted text-xs sm:ml-auto">{formatDateTime(row.at)}</span>
                </span>
                {row.email && <span className="app-muted block text-xs">{row.email}</span>}
                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm">{row.message}</p>
                {row.page && (
                  <span className="app-muted mt-1 block text-xs">Asked while on {row.page}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </>
  );
};

// ------------------------------------------------------------------ people

const PeoplePanel = ({ people, filter, onFilter, onAskAgain, askingId }) => {
  const [query, setQuery] = useState("");

  const counts = useMemo(
    () => ({
      all: people.length,
      sent: people.filter((p) => !p.due && p.lastAction === "sent").length,
      dismissed: people.filter((p) => !p.due && p.lastAction === "dismissed").length,
      due: people.filter((p) => p.due).length,
    }),
    [people]
  );

  const shown = people.filter((person) => {
    if (!matches(person, query)) return false;
    if (filter === "due") return person.due;
    if (filter === "sent" || filter === "dismissed") {
      return !person.due && person.lastAction === filter;
    }
    return true;
  });

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField value={query} onChange={setQuery} placeholder="Search person or branch…" />
        <Segmented
          options={PEOPLE_FILTERS.map((option) => ({ ...option, count: counts[option.value] }))}
          value={filter}
          onChange={onFilter}
          label="Filter people"
        />
      </div>
      <Card padded={false} className="overflow-x-auto">
        {shown.length === 0 ? (
          <EmptyState compact icon={RateReviewRoundedIcon} title="Nobody matches that." />
        ) : (
          <table className="app-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Person</th>
                <th className="text-left">Last answer</th>
                <th className="text-left">Next shown</th>
                <th className="!text-right">Sent</th>
                <th className="!text-right">Dismissed</th>
                <th className="text-left">Last on Node</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((person) => (
                <tr key={person.id}>
                  <td>
                    <span className="block font-medium">{person.name}</span>
                    <span className="app-muted block text-xs">
                      {[person.email, person.branch].filter(Boolean).join(" · ")}
                    </span>
                  </td>
                  <td>
                    <LastAnswer person={person} />
                    {person.lastActionAt && (
                      <span className="app-muted mt-0.5 block whitespace-nowrap text-xs">
                        {formatDateTime(person.lastActionAt)}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {person.due ? (
                      <Pill tone="sky">Next visit</Pill>
                    ) : (
                      formatDate(person.nextPromptAt)
                    )}
                    {person.askedAgainAt && (
                      <span className="app-muted mt-0.5 block text-xs">
                        Asked again by {person.askedAgainBy || "an admin"}
                      </span>
                    )}
                  </td>
                  <td className="text-right tabular-nums">{person.sentCount}</td>
                  <td className="text-right tabular-nums">{person.dismissedCount}</td>
                  <td className="app-muted whitespace-nowrap">
                    {person.lastSeen ? formatDateTime(person.lastSeen) : "—"}
                  </td>
                  <td className="text-right">
                    {!person.due && (
                      <button
                        type="button"
                        className="app-btn app-btn-ghost whitespace-nowrap"
                        onClick={() => onAskAgain(person)}
                        disabled={askingId === person.id}
                        title="Show them the popup on their next visit instead of waiting"
                      >
                        <ReplayRoundedIcon sx={{ fontSize: 16 }} />
                        {askingId === person.id ? "Saving…" : "Ask again"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

// -------------------------------------------------------------------- page

const MonthlyFeedback = () => {
  const user = useSelector(selectUser);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("responses");
  const [peopleFilter, setPeopleFilter] = useState("all");
  const [askingId, setAskingId] = useState(null);

  const load = useCallback(() => {
    if (!user?.id) return;
    setError(null);
    fetchFeedbackOverview(user.id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user?.id]);

  useEffect(() => load(), [load]);

  const showPeople = (filter) => {
    setPeopleFilter(filter);
    setTab("people");
  };

  const handleAskAgain = (person) => {
    setAskingId(person.id);
    askAgain(user.id, person.id)
      .then(load)
      .catch((e) => setError(e.message))
      .finally(() => setAskingId(null));
  };

  const totals = data?.totals;
  const days = data?.intervalDays || 30;

  return (
    <PageShell size="lg">
      <PageHeader
        icon={RateReviewRoundedIcon}
        tone="amber"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Monthly Feedback"
        description={`What people send from the feedback popup everyone gets every ${days} days, who dismissed it, and when each person sees it next.`}
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!data && !error && <LoadingState label="Loading feedback…" />}

      {data && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="Responses"
              value={totals.responses}
              hint={`from ${plural(totals.responders, "person", "people")}`}
              onClick={() => setTab("responses")}
            />
            <StatTile
              label="Last 30 days"
              value={totals.last30}
              tone={totals.last30 ? "emerald" : "default"}
              hint="responses"
              onClick={() => setTab("responses")}
            />
            <StatTile
              label="Dismissed"
              value={totals.dismissed}
              tone={totals.dismissed ? "amber" : "default"}
              hint="chose Not now this round"
              onClick={() => showPeople("dismissed")}
            />
            <StatTile
              label="Due now"
              value={totals.due}
              tone="sky"
              hint={`of ${plural(totals.people, "person", "people")}, on their next visit`}
              onClick={() => showPeople("due")}
            />
          </div>

          <div className="mb-4">
            <Segmented
              options={[
                { value: "responses", label: "Responses", count: totals.responses },
                { value: "people", label: "People", count: totals.people },
              ]}
              value={tab}
              onChange={setTab}
              label="Monthly feedback sections"
            />
          </div>

          {tab === "responses" && <ResponsesPanel userId={user?.id} />}
          {tab === "people" && (
            <PeoplePanel
              people={data.people}
              filter={peopleFilter}
              onFilter={setPeopleFilter}
              onAskAgain={handleAskAgain}
              askingId={askingId}
            />
          )}

          <div className="app-muted mt-6 space-y-1 text-xs">
            <p>
              The popup opens a few seconds after someone loads a page with the menu bar once their
              timer is up. Sending feedback, Not now, or the X starts a new {days}-day wait. Closing the
              tab without answering doesn't, so it asks again on their next visit.
            </p>
            <p>
              People lists everyone who has signed in to Node and isn't deactivated. Ask again shows
              the popup on that person's next visit. It doesn't open while an admin is previewing
              Node as someone else (View Node as).
            </p>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default MonthlyFeedback;
